import { format, isToday, isYesterday } from 'date-fns'
import { ROUTE_PATHS } from '@/config/routes'
import type {
  CenterKind,
  CenterMessage,
  Notification,
  NotificationDetail,
} from '@/types/notification'
import { getUserInitials, notificationCreatedAtDate } from '@/features/invitee/utils/userDrawerHelpers'

const AVATAR_TONES = [
  'bg-[#efe7fb] text-[#5b3d8a]',
  'bg-[#dce9fb] text-[#174a87]',
  'bg-[#e4f3ea] text-[#2a6b45]',
  'bg-[#fde8e4] text-[#9a3b2f]',
] as const

export type NotificationCategory = 'all' | 'mentions' | 'updates' | 'system'

function parseNotificationDate(value: number | string | null | undefined): Date | null {
  if (value == null || value === '') return null
  try {
    if (typeof value === 'string' && Number.isNaN(Number(value))) {
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    const parsed = notificationCreatedAtDate(Number(value))
    return Number.isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

/** List timestamp: Today / Yesterday / Aug 24 */
export function formatNotificationListDate(
  value: number | string | null | undefined,
): string {
  const parsed = parseNotificationDate(value)
  if (!parsed) return value == null ? '' : String(value)
  if (isToday(parsed)) return 'Today'
  if (isYesterday(parsed)) return 'Yesterday'
  return format(parsed, 'MMM d')
}

/** Detail timestamp: Today, 11:23 AM */
export function formatNotificationDetailDate(
  value: number | string | null | undefined,
): string {
  const parsed = parseNotificationDate(value)
  if (!parsed) return value == null ? '' : String(value)
  const time = format(parsed, 'h:mm a')
  if (isToday(parsed)) return `Today, ${time}`
  if (isYesterday(parsed)) return `Yesterday, ${time}`
  return `${format(parsed, 'MMM d')}, ${time}`
}

export function getSenderName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Liberty Rent'
}

export function getSenderInitials(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return getUserInitials(firstName, lastName)
}

export function getAvatarTone(name: string): string {
  const key = name.trim().toLowerCase()
  let hash = 0
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i)) % AVATAR_TONES.length
  }
  return AVATAR_TONES[hash] ?? AVATAR_TONES[0]
}

export function decodeNotificationText(value?: string | null): string {
  if (!value) return ''
  return value
    .replace(/\\n/g, ' ')
    .replace(/\\r/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export function getNotificationTitle(subject?: string | null): string {
  return decodeNotificationText(subject) || 'You were mentioned in a note'
}

export function getNotificationCategory(notesFor?: string | null): Exclude<
  NotificationCategory,
  'all'
> {
  if (notesFor === 'WHATS_NEW' || notesFor === 'TRAINING' || notesFor === 'MEETING' || notesFor === 'TODO') {
    return 'updates'
  }
  if (notesFor === 'SYSTEM') return 'system'
  return 'mentions'
}

export function getCategoryFromKind(kind: CenterKind, notesFor?: string | null): Exclude<
  NotificationCategory,
  'all'
> {
  if (kind === 'whats_new') return 'updates'
  if (kind === 'system') return 'system'
  return getNotificationCategory(notesFor)
}

export function getNotificationCategoryLabel(
  kind?: CenterKind | null,
  notesFor?: string | null,
): string {
  const category = kind
    ? getCategoryFromKind(kind, notesFor)
    : getNotificationCategory(notesFor)
  if (category === 'updates') return "What's New"
  if (category === 'system') return 'System Announcements'
  return 'Mentions'
}

export function isCenterUnread(item: Pick<CenterMessage, 'is_read'>): boolean {
  return item.is_read !== '1' && item.is_read !== 1
}

export function mapUserNotificationToCenter(item: Notification): CenterMessage {
  const sender = `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim()
  return {
    kind: 'user',
    id: item.notification_id,
    title: getNotificationTitle(item.subject),
    preview: item.notes
      ? decodeNotificationText(item.notes.replace(/<[^>]*>/g, ' '))
      : `${sender} mentioned you in a note`,
    body: item.notes,
    first_name: item.first_name,
    last_name: item.last_name,
    created_at: typeof item.created_at === 'number' ? item.created_at : Number(item.created_at) || 0,
    is_read: item.is_read,
    starred: item.starred,
    notes_for: item.notes_for,
    fk_ref_id: item.fk_ref_id,
    start_date: item.start_date,
    end_date: item.end_date,
    subject: item.subject,
    notes: item.notes,
  }
}

export function toUserNotification(item: CenterMessage): Notification {
  return {
    notification_id: item.id,
    subject: item.subject ?? item.title,
    first_name: item.first_name,
    last_name: item.last_name,
    created_at: item.created_at,
    is_read: item.is_read == null ? null : String(item.is_read),
    starred: item.starred,
    notes: item.notes ?? item.body ?? '',
    notes_for: item.notes_for ?? null,
    fk_ref_id: item.fk_ref_id ?? null,
    start_date: item.start_date ?? null,
    end_date: item.end_date ?? null,
  }
}

export function resolveCenterKind(kind?: string | null): CenterKind {
  if (kind === 'whats_new' || kind === 'system') return kind
  return 'user'
}

export function categoryFromCenterKind(
  kind: CenterKind,
): Exclude<NotificationCategory, 'all'> {
  if (kind === 'whats_new') return 'updates'
  if (kind === 'system') return 'system'
  return 'mentions'
}

export function buildProfileMessagesHref(kind: CenterKind, id: number): string {
  const params = new URLSearchParams({
    tab: 'messages',
    kind: resolveCenterKind(kind),
    id: String(id),
  })
  return `${ROUTE_PATHS.DASHBOARD_PROFILE}?${params.toString()}`
}

export function parseProfileMessageDeepLink(
  search: string | URLSearchParams,
): { kind: CenterKind; id: number } | null {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search
  const id = Number(params.get('id'))
  if (!Number.isFinite(id) || id <= 0) return null
  if (params.get('tab') && params.get('tab') !== 'messages') return null
  return { kind: resolveCenterKind(params.get('kind')), id }
}

/** Laravel may return the log object directly or wrap it in `{ data }`. */
export function unwrapNotificationDetail(payload: unknown): NotificationDetail | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  const record = payload as Record<string, unknown>
  const candidate =
    record.data && typeof record.data === 'object' && !Array.isArray(record.data)
      && ('notes' in (record.data as object) ||
        'notes_for' in (record.data as object) ||
        'notification_id' in (record.data as object) ||
        'subject' in (record.data as object) ||
        'fk_ref_id' in (record.data as object))
      ? (record.data as Record<string, unknown>)
      : record
  if (
    !('notes' in candidate) &&
    !('notes_for' in candidate) &&
    !('notification_id' in candidate) &&
    !('subject' in candidate) &&
    !('fk_ref_id' in candidate)
  ) {
    return null
  }
  return candidate as unknown as NotificationDetail
}

/**
 * Merge extra mention-detail fields without overwriting list sender/date
 * with Mailgun log columns (created_at, first_name, etc.).
 */
export function mergeNotificationDetail(
  base: Notification,
  detail?: Partial<NotificationDetail> | null,
): NotificationDetail {
  if (!detail) return { ...base }
  return {
    ...base,
    subject: detail.subject ?? base.subject,
    notes: detail.notes ?? base.notes,
    notes_for: detail.notes_for ?? base.notes_for,
    fk_ref_id: detail.fk_ref_id ?? base.fk_ref_id,
    start_date: detail.start_date ?? base.start_date,
    end_date: detail.end_date ?? base.end_date,
    ref_name: detail.ref_name,
    ref_user_id: detail.ref_user_id,
    new_id: detail.new_id,
    previous_id: detail.previous_id,
    current: detail.current,
    total: detail.total,
    details: detail.details,
  }
}

export function processReleaseNoteImages(html: string): string {
  if (!html.includes('<img')) return html
  return html.replace(/<img/g, '<img class="versionimage max-w-full h-auto" alt="filepreviewpopup"')
}
